// File: src/components/OmniCine/Timeline/XR/XRVideoIntentBridge.tsx
//
// XRVideoIntentBridge (Phase V1 — Video: select/focus only)
// - Listens for XR clip intents (select/focus)
// - Dispatches a canonical, low-risk selection action
// - Guards: only allows selecting clips that belong to a VIDEO track
// - Optional XRSessionAuthority gate (enabled by default for select-only)

import React, { useEffect, useMemo, useRef } from "react";
import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";
import { XRSessionAuthority } from "@/xr/session/XRSessionAuthority";

type XRClipIntentDetail = {
  controllerId: string;
  clipId: string;
  source?: string; // "xr-user" recommended
};

const EVT_SELECT = "omnicine:xr:clip:select";
const EVT_FOCUS = "omnicine:xr:clip:focus"; // alias

const XR_AUTH = new XRSessionAuthority("standalone", {
  enabled: true,
  requireToken: false,
  allowedOps: ["clip/select"],
  onDeny: (_reason, _intent) => {
    // console.warn("[XR_VIDEO_AUTH] deny:", _reason, _intent);
  },
});

function normalizeDetail(raw: unknown): XRClipIntentDetail | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;

  const controllerId = String(r.controllerId ?? "").trim();
  const clipId = String(r.clipId ?? "").trim();
  if (!controllerId || !clipId) return null;

  const source = r.source != null ? String(r.source) : "xr-user";
  return { controllerId, clipId, source };
}

export default function XRVideoIntentBridge(): null {
  const { state, dispatch } = useOmniCineTimeline();

  // Keep fresh state for event listeners without rebinding
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const api = useMemo(() => {
    const isVideoClip = (clipId: string): boolean => {
      const st = stateRef.current;
      const clip = st.clips?.find((c: any) => c?.id === clipId);
      if (!clip) return false;

      const track = st.tracks?.find((t: any) => t?.id === clip.trackId);
      return Boolean(track && track.kind === "video");
    };

    const allow = (controllerId: string, clipId: string): boolean => {
      XR_AUTH.registerClient(controllerId);

      // Pre-check: only video clips
      if (!isVideoClip(clipId)) return false;

      return XR_AUTH.validateXRIntent({
        op: "clip/select",
        // Bind to the clip's trackId for future tightening (optional)
        trackId: (stateRef.current.clips as any[])?.find((c) => c?.id === clipId)?.trackId,
        meta: { source: "xr-user", clientId: controllerId },
      });
    };

    const select = (d: XRClipIntentDetail) => {
      if (!allow(d.controllerId, d.clipId)) return;

      // Canonical selection action. If your reducer already supports this name, great.
      // If not, add the reducer case (Step 2 below) — it’s non-breaking.
      dispatch({
        type: "clip/select",
        payload: { clipId: d.clipId },
        meta: { source: d.source ?? "xr-user", note: "xr clip select" },
      } as any);
    };

    return { select };
  }, [dispatch]);

  useEffect(() => {
    const onSelect = (ev: Event) => {
      const d = normalizeDetail((ev as CustomEvent).detail);
      if (!d) return;
      api.select(d);
    };

    window.addEventListener(EVT_SELECT, onSelect as EventListener);
    window.addEventListener(EVT_FOCUS, onSelect as EventListener);

    return () => {
      window.removeEventListener(EVT_SELECT, onSelect as EventListener);
      window.removeEventListener(EVT_FOCUS, onSelect as EventListener);
    };
  }, [api]);

  return null;
}
